import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { authApi } from "../api/endpoints";
import { useSession } from "../hooks/useSession";
import { AnimatedTitle } from "../components/AnimatedTitle";
import "../theme/twoFactor.css";

/** Server-bound pending challenge survives reload without storing credentials in the browser. */
export default function TwoFactorPage() {
  const { t } = useTranslation();
  const { verifySignIn } = useSession();
  const navigate = useNavigate(), location = useLocation();
  const [challenge, setChallenge] = useState(null);
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [error, setError] = useState(null), [expired, setExpired] = useState(false);
  const [pending, setPending] = useState(false), [sending, setSending] = useState(false);
  const [resendAt, setResendAt] = useState(0), [clock, setClock] = useState(() => Date.now());
  const inputs = useRef([]), verifying = useRef(false);
  const code = digits.join("");
  const seconds = Math.max(0, Math.ceil((resendAt - clock) / 1000));

  useEffect(() => {
    let active = true;
    authApi.pendingVerification().then((data) => {
      if (!active) return;
      setChallenge(data); setClock(Date.now()); setResendAt(Date.now() + data.resendAfterSeconds * 1000);
    }).catch((failure) => { if (active) { setError(failure.code); setExpired(true); } });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (challenge?.challengeId) inputs.current[0]?.focus();
  }, [challenge?.challengeId]);
  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const timedOut = expired || (challenge && clock >= new Date(challenge.expiresAt).getTime());
  const unavailable = pending || sending || !challenge || timedOut;

  async function verify(value) {
    if (unavailable || verifying.current || !/^\d{6}$/.test(value)) return;
    verifying.current = true; setPending(true); setError(null);
    try {
      await verifySignIn({ challengeId: challenge.challengeId, code: value });
      const target = location.state?.from;
      navigate(typeof target === "string" && target.startsWith("/") && !target.startsWith("//") ? target : "/inicio", { replace: true });
    } catch (failure) {
      setError(failure.code ?? "errors.unexpected");
      if (["errors.twoFactorExpired", "errors.twoFactorLocked", "errors.accountBlocked"].includes(failure.code)) setExpired(true);
      setDigits(Array(6).fill(""));
      setPending(false); verifying.current = false;
      requestAnimationFrame(() => inputs.current[0]?.focus());
    }
  }
  function update(index, raw) {
    const value = raw.replace(/\D/g, "");
    if (value.length > 1) { paste(value); return; }
    const next = digits.slice(); next[index] = value; setDigits(next); setError(null);
    if (value) inputs.current[Math.min(index + 1, 5)]?.focus();
    if (next.every(Boolean)) void verify(next.join(""));
  }
  function paste(raw) {
    const value = raw.replace(/\D/g, "").slice(0, 6);
    if (!value) return;
    const next = Array.from({ length: 6 }, (_, i) => value[i] ?? "");
    setDigits(next); setError(null); inputs.current[Math.min(value.length, 5)]?.focus();
    if (value.length === 6) void verify(value);
  }
  async function resend() {
    if (unavailable || seconds > 0) return;
    setSending(true); setError(null);
    try {
      const data = await authApi.resendVerification(challenge.challengeId);
      setChallenge(data); setResendAt(Date.now() + data.resendAfterSeconds * 1000); setClock(Date.now());
      setDigits(Array(6).fill(""));
    } catch (failure) {
      setError(failure.code ?? "errors.unexpected");
      if (["errors.twoFactorExpired", "errors.twoFactorDelivery", "errors.twoFactorLocked"].includes(failure.code)) setExpired(true);
    } finally { setSending(false); }
  }
  return <Box className="adl-two-factor-shell">
    <i className="adl-two-factor-tube first" aria-hidden="true" /><i className="adl-two-factor-tube last" aria-hidden="true" />
    <Box className="adl-two-factor-content">
    <img className="adl-two-factor-mobile-logo" src="/brand/adaltra_neg.svg" alt="ADALTRA" width="160" height="36" />
    <Box className="adl-two-factor-card" component="section" aria-labelledby="two-factor-title">
      <img src="/brand/avatar_v.svg" alt="" width="46" height="46" />
      <Typography component="h1" id="two-factor-title" sx={{ fontSize: 21, fontWeight: 800, color: "#000" }}><AnimatedTitle text={t("twoFactor.title")} /></Typography>
      {!challenge && !error && <CircularProgress size={24} aria-label={t("common.loading")} />}
      {timedOut ? <Typography role="status" variant="body2" sx={{ color: "#666" }}>{t("twoFactor.expired")}</Typography> : challenge && <>
        <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.7 }}>{t("twoFactor.body")}<br /><strong style={{ color: "#463a86" }}>{challenge.email}</strong></Typography>
        <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); void verify(code); }} className="adl-two-factor-form">
          <Box role="group" aria-label={t("twoFactor.codeLabel")} className="adl-otp" onPaste={(event) => { event.preventDefault(); paste(event.clipboardData.getData("text")); }}>
            {digits.map((digit, index) => <input key={index} ref={(el) => { inputs.current[index] = el; }} type="text" inputMode="numeric" autoComplete={index === 0 ? "one-time-code" : "off"} maxLength={6} value={digit} disabled={Boolean(unavailable)} aria-label={`${t("twoFactor.codeLabel")} ${index + 1}/6`} aria-invalid={Boolean(error)} className={digit ? "is-filled" : ""}
              onFocus={(event) => event.target.select()} onChange={(event) => update(index, event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Backspace" && !digit && index > 0) { event.preventDefault(); update(index - 1, ""); inputs.current[index - 1]?.focus(); }
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") { event.preventDefault(); inputs.current[Math.max(0, Math.min(5, index + (event.key === "ArrowLeft" ? -1 : 1)))]?.focus(); }
              }} />)}
          </Box>
          {error && <Alert severity="error">{t(error, { defaultValue: t("errors.unexpected") })}</Alert>}
          <Button variant="contained" fullWidth type="submit" disabled={Boolean(unavailable || code.length !== 6)} sx={{ borderRadius: 99 }}>{pending ? <CircularProgress size={19} color="inherit" aria-label={t("common.loading")} /> : t("twoFactor.submit")}</Button>
          <Typography variant="caption" sx={{ color: "#8d83b1" }}>{t("twoFactor.notReceived")} <Button type="button" variant="text" size="small" onClick={resend} disabled={Boolean(unavailable || seconds > 0)} sx={{ color: "#e117c1", minHeight: 44, px: 0.5 }}>{sending ? t("common.loading") : seconds > 0 ? t("twoFactor.resendIn", { time: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` }) : t("twoFactor.resend")}</Button></Typography>
        </Box>
      </>}
      {timedOut && error && <Alert severity="error">{t(error, { defaultValue: t("errors.unexpected") })}</Alert>}
      <Button component={RouterLink} to="/" replace variant="text" sx={{ fontSize: 12, minHeight: 44 }}><span style={{ color: "#e117c1", marginRight: 6 }}>/</span>{t("auth.backToSignIn")}</Button>
    </Box>
    </Box>
  </Box>;
}
